//#region Libraries
import { useState } from "react";
import { useNavigate } from "react-router";
//#endregion
//#region Styles
import { Container } from "./styles";
//#endregion
//#region Components
import Preloader from "components/Preloader/Preloader";
import CustomButton from "components/CustomButton/CustomButton";
import DialogAlertSuccessNotify from "components/DialogAlertNotify/DialogAlertSuccessNotify/DialogAlertSuccessNotify";
import DialogAlertAction from "components/DialogAlertAction/DialogAlertAction";
import DialogShowErrorInfoAtTheEndOfCreation from "../DialogShowErrorInfoAtTheEndOfCreation/DialogShowErrorInfoAtTheEndOfCreation";
//#endregion
//#region Services
import {
	requestCreateAcadReinforceMassiveLoading,
	requestValidateFileForCreateAcadReinforceMassiveLoading,
} from "services/acadReinforce/aboutAcadReinforce";
//#endregion
//#region Utils
import { useNavigatingAway } from "utils/hooks/usePrompt";
import useMainContext from "utils/context/useMainContext";
import ABSOLUTE_PATHS from "routes/utils/absolutePaths";
import { wait } from "utils/others";
import useLoadingRequest from "components/Preloader/utils/hooks/useLoadingRequest";
import useShowDialogAlert from "components/DialogAlert/utils/hooks/useDialogAlert";
//#endregion

const Footer = ({
	form,
	changeErrors,
	currentStep,
	setMustCorrect,
	fillErrorStack,
	errorStackSize,
	rowCountFile,
	toStep,
	canShowDialogLeavingPage,
}) => {
	//#region Extra
	const navigate = useNavigate();
	const mainContext = useMainContext();
	const loadingRequest = useLoadingRequest();
	//#endregion
	//#region States
	const [
		errorInformationAtTheEndOfCreation,
		setErrorInformationAtTheEndOfCreation,
	] = useState(null);
	//#endregion
	//#region Dialogs
	const dialogShowErrorInformationAtTheEndOfCreation = useShowDialogAlert();
	const dialogShowCreatedAcadReinforce = useShowDialogAlert(
		(toggle, value) => {
			toggle();
			if (value === false) toAcadReinforceList();
		}
	);
	//#endregion
	//#region Functions
	//#region Manejo de salida de vista
	const [showDialogLeavingPage, confirmNavigation, cancelNavigation] =
		useNavigatingAway(canShowDialogLeavingPage);
	//#endregion
	const toAcadReinforceList = () => {
		setTimeout(() => {
			navigateToAcadReinforce();
			confirmNavigation();
		}, 380);
	};
	const navigateToAcadReinforce = () => {
		navigate(ABSOLUTE_PATHS.ACAD_REINFORCE_LIST);
	};
	//#region Validations
	//#endregion
	const showMustCorrectMessage = async () => {
		setMustCorrect(true);
		await wait(2400);
		setMustCorrect(false);
	};
	const scrollToTop = () => {
		window.scrollTo({ top: 0, behavior: "smooth" });
	};
	const navigationHandler = async () => {
		const result = form.isValid();
		const { isValid, newErrors } = result;
		changeErrors(newErrors);
		if (!isValid) {
			showMustCorrectMessage();
			scrollToTop();
			return;
		}
		loadingRequest.show("Cargando archivo...");
		try {
			const fetchErrorStack =
				await requestValidateFileForCreateAcadReinforceMassiveLoading(
					form.value.fileData.base64
				);
			if (fetchErrorStack === null) {
				mainContext.setShowDialogUnexpectedError(true);
				return;
			}
			const { errorStack, rowCountFile } = fetchErrorStack;
			fillErrorStack({
				stack: errorStack,
				rowCountFile,
			});
			if (errorStack.length > 0) {
				toStep(2);
				form.reset();
			} else toStep(3);
			scrollToTop();
		} catch (err) {
			mainContext.setShowDialogUnexpectedError(true);
			return;
		} finally {
			loadingRequest.hide();
		}
	};
	const finalizeMassiveLoading = async () => {
		loadingRequest.show("Guardando refuerzos...");
		const data = await requestCreateAcadReinforceMassiveLoading(
			form.value.fileData.base64
		);
		loadingRequest.hide();
		const { success } = data;
		if (success) {
			dialogShowCreatedAcadReinforce.set(true);
			return;
		}
		const { row, message } = data;
		if (row || message) {
			mainContext.setShowDialogUnexpectedError(true);
			mainContext.setDialogUnexpectedErrorMessage(
				`Fila: ${row} | ${message || "Ocurrió un error inesperado."}`
			);
		} else {
			const { errorStack, quantities } = data;
			setErrorInformationAtTheEndOfCreation({
				errorStack,
				quantities,
			});
			if (quantities.error > 0) form.reset();
			dialogShowErrorInformationAtTheEndOfCreation.toggle();
		}
		toStep(1);
	};
	const getTitleDialogAcadReinforce = () => {
		return `Hemos creado ${rowCountFile} refuerzo${
			rowCountFile > 1 ? "s" : ""
		}`;
	};
	//#endregion
	return (
		<>
			<Container>
				<div>
					<CustomButton
						text="Cancelar"
						variant="sixtinary"
						onClick={() => navigateToAcadReinforce()}
					/>
				</div>
				{(currentStep === 1 ||
					(currentStep > 1 &&
						(errorStackSize > 0 || rowCountFile === 0))) && (
					<CustomButton
						text="Continuar"
						onClick={navigationHandler}
					/>
				)}
				{currentStep > 2 && rowCountFile > 0 && (
					<CustomButton
						text="Finalizar"
						onClick={finalizeMassiveLoading}
					/>
				)}
			</Container>
			<Preloader {...loadingRequest.value} />
			{errorInformationAtTheEndOfCreation && (
				<DialogShowErrorInfoAtTheEndOfCreation
					{...errorInformationAtTheEndOfCreation}
					dialogShow={dialogShowErrorInformationAtTheEndOfCreation}
				/>
			)}
			<DialogAlertAction
				show={showDialogLeavingPage}
				toggleShow={cancelNavigation}
				title="¿Deseas salir sin guardar tu refuerzo?"
				content="Recuerda que al salir, la información que has escrito recientemente, no se guardará"
			>
				<CustomButton
					variant="quintinary"
					text="Sí, salir"
					width="130px"
					onClick={confirmNavigation}
				/>
				<CustomButton
					variant="quaternary"
					text="No, continuar"
					width="130px"
					onClick={cancelNavigation}
				/>
			</DialogAlertAction>
			<DialogAlertSuccessNotify
				show={dialogShowCreatedAcadReinforce.value}
				toggleShow={dialogShowCreatedAcadReinforce.toggle}
				title={getTitleDialogAcadReinforce()}
				subtitle={`Podrás verlo en el listado de "Refuerzos"`}
				description="IMPORTANTE: Recuerda revisar detalladamente toda la información de tus refuerzos cargados."
				buttons={
					<CustomButton
						variant="quaternary"
						text="Ir al listado de refuerzos"
						onClick={() =>
							dialogShowCreatedAcadReinforce.toggle(false)
						}
					/>
				}
			/>
		</>
	);
};

export default Footer;
